import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import ThemeButtonFloating from 'assets/components/button/themes/Theme.Button.Floating';
import ThemeButtonMenuItem from 'assets/components/button/themes/Theme.Button.MenuItem';
import Icon from 'assets/components/icon/Icon';
import { addProgressToWindow } from 'assets/components/progressWindow/ProgressWindow';
import { getLocales } from 'assets/locales/Locale';
import DropdownButton from 'assets/templates/dropdownButton/DropdownButton';
import ThemeDropdownButtonPopup from 'assets/templates/dropdownButton/themes/Theme.DropdownButton.Popup';
import Theme from 'assets/themes/Theme.Common';
import { isEmpty } from 'lodash';
import { ContractSigningTypes } from 'models/enums/ContractSigningTypes';
import { isMobile } from 'react-device-detect';
import { SalesContractVertaforeSyncType, useSalesContractProvider } from '../Sales.Contract.Hooks';

export default function SalesContractActions() {
  const { lang } = getLocales();
  const {
    listApi,
    permissions,
    contractUnknownVertaforeStatusListApi,
    contractNotUploadedVertaforeStatusListApi,
    exportContractsToExcel,
    syncAllWithVertafore,
    resendAllForSigning,
    reuploadAllSignedDocuments,
    importMissingSigningCertificates,
    importSignedDocumentationAggregates,
    isFetchingEnvelopes,
    selectedContracts,
    hasMixedDealers,
    createStatementFromSelectedContracts,
  } = useSalesContractProvider();

  return (
    <>
      <DropdownButton
        buttonMedia={<Icon class="fas-sync" />}
        horizontalPosition="auto_fixed"
        verticalPosition="auto_fixed"
        subcomponents={ThemeDropdownButtonPopup}
        buttonBody={lang.syncWithVertafore}
        htmlElementProps={{ title: lang.syncAllWithVertafore }}
        title={lang.syncAllWithVertafore}
        disabled={!permissions.VERTAFORE_SYNC_CONTRACT}
        tabIndex={null}
        dropdownOptions={() => {
          return (
            <>
              <Button
                class={ThemeButtonMenuItem}
                render={lang.contracts}
                onClick={({ addProgressToWindow, syncType }) => syncAllWithVertafore(addProgressToWindow, syncType)}
                data={{ addProgressToWindow, syncType: SalesContractVertaforeSyncType.SYNC_CONTRACTS }}
                disabled={
                  !permissions.VERTAFORE_SYNC_CONTRACT || isEmpty(contractUnknownVertaforeStatusListApi?.payload)
                }
              />
              <Button
                class={ThemeButtonMenuItem}
                render={lang.contractsDocumentationAggregate}
                onClick={({ addProgressToWindow, syncType }) => syncAllWithVertafore(addProgressToWindow, syncType)}
                disabled={
                  !permissions.VERTAFORE_SYNC_CONTRACT || isEmpty(contractNotUploadedVertaforeStatusListApi?.payload)
                }
                data={{ addProgressToWindow, syncType: SalesContractVertaforeSyncType.SYNC_CONTRACT_DOCUMENTS }}
              />
            </>
          );
        }}
      />
      <DropdownButton
        buttonMedia={<Icon class="fas-sync" />}
        horizontalPosition="auto_fixed"
        verticalPosition="auto_fixed"
        subcomponents={ThemeDropdownButtonPopup}
        buttonBody={lang.signingOptions}
        htmlElementProps={{ title: lang.signingOptions }}
        title={lang.signingOptions}
        disabled={!(permissions?.SEND_CONTRACT_FOR_SIGNING || permissions?.REUPLOAD_SIGNED_CONTRACTDOCUMENT)}
        tabIndex={null}
        dropdownOptions={({ animateOut }) => {
          const isReuploadDisabled = !permissions.REUPLOAD_SIGNED_CONTRACTDOCUMENT || isEmpty(listApi?.payload?.data);
          const isResendDisabled = !permissions.SEND_CONTRACT_FOR_SIGNING || isEmpty(listApi?.payload?.data);
          const defaultButtonProps = {
            class: ThemeButtonMenuItem,
            isLoading: isFetchingEnvelopes,
            style: { fontSize: Theme.Size.S },
          };
          return (
            <>
              <Button
                {...defaultButtonProps}
                render={!isMobile && `${lang.resendForSigning} (${lang.DocuSign})`}
                onClick={(data) => {
                  animateOut();
                  resendAllForSigning(data);
                }}
                data={{ onSaveProgress: addProgressToWindow, signingType: ContractSigningTypes.DocuSign }}
                disabled={isResendDisabled}
              />
              <Button
                {...defaultButtonProps}
                render={!isMobile && `${lang.reuploadSignedDocuments} (${lang.DocuSign})`}
                onClick={(data) => {
                  animateOut();
                  reuploadAllSignedDocuments(data);
                }}
                data={{ onSaveProgress: addProgressToWindow, signingType: ContractSigningTypes.DocuSign }}
                disabled={isReuploadDisabled}
              />
              <Button
                {...defaultButtonProps}
                render={!isMobile && `${lang.resendForSigning} (${lang.SignFlow})`}
                onClick={(data) => {
                  animateOut();
                  resendAllForSigning(data);
                }}
                data={{ onSaveProgress: addProgressToWindow, signingType: ContractSigningTypes.SignFlow }}
                disabled={isResendDisabled}
              />
              <Button
                {...defaultButtonProps}
                render={!isMobile && `${lang.reuploadSignedDocuments} (${lang.SignFlow})`}
                onClick={(data) => {
                  animateOut();
                  reuploadAllSignedDocuments(data);
                }}
                data={{ onSaveProgress: addProgressToWindow, signingType: ContractSigningTypes.SignFlow }}
                disabled={isReuploadDisabled}
              />
              <Button
                {...defaultButtonProps}
                render={!isMobile && `${lang.importMissingSigningCertificates} (${lang.SignFlow})`}
                onClick={(data) => {
                  animateOut();
                  importMissingSigningCertificates(data);
                }}
                data={{ onSaveProgress: addProgressToWindow }}
                disabled={isReuploadDisabled}
              />
              <Button
                {...defaultButtonProps}
                render={!isMobile && `${lang.importSignedDocumentationAggregates} (${lang.SignFlow})`}
                onClick={(data) => {
                  animateOut();
                  importSignedDocumentationAggregates(data);
                }}
                data={{ onSaveProgress: addProgressToWindow }}
                disabled={isReuploadDisabled}
              />
            </>
          );
        }}
      />
      <Button
        class={isMobile ? ThemeButtonFloating : ThemeButtonCircle}
        active
        media={<Icon class="fas-download" />}
        render={!isMobile && lang.export}
        onClick={exportContractsToExcel}
        disabled={!permissions.CREATE_CONTRACT || isEmpty(listApi?.payload?.data)}
      />
      <Button
        class={isMobile ? ThemeButtonFloating : ThemeButtonCircle}
        active
        media={<Icon class="fas-file" />}
        render={!isMobile && lang.createStatement}
        onClick={createStatementFromSelectedContracts}
        disabled={
          selectedContracts?.length === 0 || hasMixedDealers || !permissions.CREATE_STATEMENTS_FROM_CONTRACT_LIST
        }
        htmlElementProps={{
          title:
            selectedContracts?.length === 0
              ? lang.selectAtLeastOneContract
              : hasMixedDealers
                ? lang.contractsMustBelongToSameDealer
                : !permissions.CREATE_STATEMENTS_FROM_CONTRACT_LIST
                  ? lang.missingPermission
                  : lang.createStatement,
        }}
      />
    </>
  );
}
